import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import dayjs, { Dayjs } from "dayjs";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { days, DayType, OpenHours } from "@/app/Offices/Utils/days";
import { GenericField } from "@/lib/Components/Form/Fields/GenericField";
import { Switch } from "@headlessui/react";
import { Button } from "@/components/catalyst/button";

export function OpenHoursInput({
  prefix,
  className,
  disabled = false,
}: {
  prefix: string;
  className?: string;
  label?: string;
  disabled?: boolean;
}) {
  return (
    <div className={cn("col-span-full space-y-3", className)}>
      <div className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {days.map((day, index) => (
          <ToFromSelect
            day={day}
            prefix={prefix}
            key={day}
            isFirst={index === 0}
            isLast={index === days.length - 1}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}

function ToFromSelect({
  day,
  prefix,
  disabled = false,
}: {
  day: DayType;
  prefix: string;
  isFirst?: boolean;
  isLast?: boolean;
  disabled?: boolean;
}) {
  const { setFieldValue, values } = useFormState<Record<string, OpenHours>>();

  const value = values[prefix]?.[day]?.at(0);
  const startTime = value?.split("-")[0] ?? null;
  const endTime = value?.split("-")[1] ?? null;
  const enabled = values[prefix]?.[day]?.length > 0;

  return (
    <div
      className={cn(
        "group transition-all duration-200",
        enabled ? "bg-white" : "bg-slate-50/50",
        !disabled && "hover:bg-slate-50/30",
      )}
    >
      <GenericField
        name={`${prefix}.${day}`}
        label={
          <span
            className={cn(
              "font-medium capitalize transition-colors duration-200",
              enabled ? "text-slate-900" : "text-slate-500",
            )}
          >
            {day}
          </span>
        }
        className="px-6 py-4"
        viewNode={<ViewNode value={values[prefix]?.[day]} />}
        isEditable={!disabled}
      >
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <Switch
              checked={enabled}
              disabled={disabled}
              onChange={(checked) => {
                if (checked) {
                  setFieldValue(`${prefix}.${day}`, ["09:00-17:00"]);
                } else {
                  setFieldValue(`${prefix}.${day}`, []);
                }
              }}
              className={cn(
                "relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200",
                "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                enabled
                  ? "bg-gradient-to-r from-blue-500 to-blue-600"
                  : "bg-slate-200",
                disabled && "cursor-not-allowed opacity-50",
              )}
            >
              <span
                className={cn(
                  "inline-block h-4 w-4 transform rounded-full bg-white transition-all duration-200",
                  "shadow-sm",
                  enabled ? "translate-x-6" : "translate-x-1",
                )}
              />
            </Switch>
            <span
              className={cn(
                "text-xs font-medium transition-colors duration-200",
                enabled ? "text-blue-600" : "text-slate-400",
              )}
            >
              {enabled ? "Open" : "Closed"}
            </span>
          </div>

          {enabled && (
            <>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <fieldset
                    className={cn(
                      "flex items-center gap-2",
                      startTime === "00:00" &&
                        endTime === "24:00" &&
                        "pointer-events-none opacity-50",
                    )}
                  >
                    <TimeSelect
                      value={startTime}
                      onSelect={(newStart) => {
                        setFieldValue(`${prefix}.${day}`, [
                          `${newStart}-${endTime}`,
                        ]);
                      }}
                      placeholder="Open"
                      className="w-36"
                      disabled={
                        disabled ||
                        (startTime === "00:00" && endTime === "24:00")
                      }
                    />
                    <span className="font-medium text-slate-400">to</span>
                    <TimeSelect
                      value={endTime}
                      relativeTo={startTime ?? undefined}
                      onSelect={(newEnd) => {
                        setFieldValue(`${prefix}.${day}`, [
                          `${startTime}-${newEnd}`,
                        ]);
                      }}
                      placeholder="Close"
                      className="w-36"
                      disabled={
                        disabled ||
                        (startTime === "00:00" && endTime === "24:00")
                      }
                    />
                  </fieldset>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`${prefix}-${day}-24hrs`}
                    checked={startTime === "00:00" && endTime === "24:00"}
                    disabled={disabled}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFieldValue(`${prefix}.${day}`, ["00:00-24:00"]);
                      } else {
                        setFieldValue(`${prefix}.${day}`, ["09:00-17:00"]);
                      }
                    }}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <label
                    htmlFor={`${prefix}-${day}-24hrs`}
                    className={cn(
                      "cursor-pointer text-sm font-medium transition-colors duration-200",
                      startTime === "00:00" && endTime === "24:00"
                        ? "text-blue-600"
                        : "text-slate-600",
                      disabled && "cursor-not-allowed",
                    )}
                  >
                    24hrs
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Button
                  onClick={() => {
                    const newHours = {
                      monday: [`${startTime}-${endTime}`],
                      tuesday: [`${startTime}-${endTime}`],
                      wednesday: [`${startTime}-${endTime}`],
                      thursday: [`${startTime}-${endTime}`],
                      friday: [`${startTime}-${endTime}`],
                      saturday: [`${startTime}-${endTime}`],
                      sunday: [`${startTime}-${endTime}`],
                    };
                    setFieldValue(prefix, newHours);
                  }}
                  className={cn(
                    "opacity-0 transition-all duration-200 group-hover:opacity-100",
                  )}
                  plain
                  disabled={disabled}
                >
                  Apply to all
                </Button>
              </div>
            </>
          )}
        </div>
      </GenericField>
    </div>
  );
}

function ViewNode({ value }: { value: string[] | undefined }) {
  if (!value || value.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-sm font-medium text-slate-600">
        <div className="h-2 w-2 rounded-full bg-slate-400" />
        Closed
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2">
      {value.map((time: any) => {
        const start = dayjs(time.split("-")[0], "HH:mm").format("h:mma");
        const endTime = time.split("-")[1];
        const end =
          endTime === "24:00"
            ? "12:00am"
            : dayjs(endTime, "HH:mm").format("h:mma");

        return (
          <span
            key={time}
            className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-sm font-medium text-green-700"
          >
            <div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
            {start} - {end}
          </span>
        );
      })}
      <span className="text-xs text-slate-500">(from supplier)</span>
    </div>
  );
}

type TimeSelectProps = {
  relativeTo?: string;
  value: string | null;
  onSelect: (value: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
};

export function TimeSelect({
  relativeTo: rt,
  value,
  onSelect,
  className,
  placeholder = "Select time",
  disabled = false,
}: TimeSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        if (!contentRef.current) return;
        const targetValue = value ?? "08:00";
        const el = contentRef.current.querySelector(
          `[data-value="${targetValue}"]`,
        );
        el?.scrollIntoView({ block: "start" });
      });
    }
  }, [open]);

  const relativeTo = rt ? dayjs(rt, "HH:mm") : undefined;

  const startTime = relativeTo
    ? dayjs()
        .set("hours", relativeTo.hour())
        .set("minutes", relativeTo.minute())
    : dayjs().startOf("day");

  const minsInDay = 1440;
  const minutes = startTime.hour() * 60 + startTime.minute();
  const minutesRemaining = minsInDay - minutes;
  const numSlots = Math.floor(minutesRemaining / 30);

  const times: {
    value: string;
    label: string;
  }[] = Array.from({ length: numSlots }).map((_, i) => {
    const newTime = startTime.add(i * 30, "minute");
    return {
      value: newTime.format("HH:mm"),
      label: formatLabel(newTime, relativeTo),
    };
  });

  // Add midnight option for end times (24:00)
  if (relativeTo) {
    times.push({
      value: "24:00",
      label: "12:00 AM (+24h)",
    });
  }

  const filteredTimes = (() => {
    if (!search) return times;

    const searchLower = search.toLowerCase().trim();

    // Parse AM/PM indicator from end of search (e.g. "5p", "5pm", "5 pm")
    const meridiemMatch = searchLower.match(/\s*(a|p)\s*m?\s*$/);
    const meridiem = meridiemMatch
      ? meridiemMatch[1] === "a"
        ? "AM"
        : "PM"
      : null;
    const numericPart = searchLower.replace(/\s*(a|p)\s*m?\s*$/, "").trim();

    let searchHour: number | null = null;
    let searchMinutes: string | null = null;

    if (numericPart.includes(":")) {
      const [h, m] = numericPart.split(":");
      searchHour = parseInt(h, 10);
      searchMinutes = m || null;
    } else if (numericPart) {
      searchHour = parseInt(numericPart, 10);
    }

    if (
      searchHour === null ||
      isNaN(searchHour) ||
      searchHour < 1 ||
      searchHour > 12
    ) {
      return times.filter((t) => t.label.toLowerCase().includes(searchLower));
    }

    const filtered = times.filter((time) => {
      if (time.value === "24:00") {
        return searchHour === 12 && (!meridiem || meridiem === "AM");
      }

      const timeDayjs = dayjs(time.value, "HH:mm");
      const hour12 = timeDayjs.hour() % 12 || 12;
      const timeMeridiem = timeDayjs.format("A");
      const timeMinutesStr = timeDayjs.format("mm");

      if (hour12 !== searchHour) return false;
      if (meridiem && timeMeridiem !== meridiem) return false;
      if (searchMinutes !== null && !timeMinutesStr.startsWith(searchMinutes))
        return false;

      return true;
    });

    // Sort: on-the-hour times first, preserving chronological order within groups
    filtered.sort((a, b) => {
      const aMin =
        a.value === "24:00" ? 0 : parseInt(a.value.split(":")[1], 10);
      const bMin =
        b.value === "24:00" ? 0 : parseInt(b.value.split(":")[1], 10);
      if (aMin === 0 && bMin !== 0) return -1;
      if (aMin !== 0 && bMin === 0) return 1;
      return 0;
    });

    // Add custom time if search parses to a valid time not already in the list
    const formats = ["H:mm", "h:mma", "h:mm a", "ha", "h a"];
    for (const fmt of formats) {
      const parsed = dayjs(search, fmt);
      if (parsed.isValid()) {
        const newTime = dayjs()
          .set("hours", parsed.hour())
          .set("minutes", parsed.minute());

        if (relativeTo && newTime.isBefore(relativeTo)) break;
        if (!filtered.some((t) => t.value === newTime.format("HH:mm"))) {
          filtered.unshift({
            value: newTime.format("HH:mm"),
            label: formatLabel(newTime, relativeTo),
          });
        }
        break;
      }
    }

    return filtered;
  })();

  return (
    <Popover
      open={disabled ? false : open}
      onOpenChange={disabled ? undefined : setOpen}
    >
      <PopoverTrigger asChild>
        <Button
          outline
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "shadow-xs justify-between border-gray-300 text-sm font-normal transition-shadow duration-100",
            "focus:border-blue-500 focus:ring-blue-500",
            !value && "text-gray-500",
            disabled && "cursor-not-allowed opacity-50",
            className,
          )}
        >
          {value ? (
            <span className="text-slate-900">
              {value === "24:00"
                ? "12:00 AM"
                : dayjs(value, "HH:mm").format("h:mm A")}
            </span>
          ) : (
            placeholder
          )}
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        className="w-[280px] border-slate-200 p-0 shadow-lg"
        align="start"
      >
        <Command shouldFilter={false} className="rounded-lg">
          <CommandInput
            placeholder="Type a time..."
            onValueChange={(newVal) => {
              setSearch(newVal);
            }}
            className="border-0 focus:ring-0"
          />
          <CommandEmpty className="py-6 text-center text-sm text-slate-500">
            No matching time found
          </CommandEmpty>
          <CommandList className="max-h-[300px]">
            <CommandGroup>
              {filteredTimes.map((time) => (
                <CommandItem
                  key={time.value}
                  value={time.value}
                  onSelect={(currentValue) => {
                    onSelect(currentValue);
                    setOpen(false);
                    setSearch("");
                  }}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2.5",
                    "cursor-pointer hover:bg-slate-50",
                    "transition-colors duration-150",
                  )}
                >
                  <Check
                    className={cn(
                      "h-4 w-4 text-blue-600",
                      value === time.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <Clock className="h-3.5 w-3.5 text-slate-400" />
                  <span
                    className={cn(
                      "flex-1 whitespace-nowrap",
                      value === time.value && "font-medium text-blue-600",
                    )}
                  >
                    {time.label}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function formatLabel(time: Dayjs, relativeTo?: Dayjs) {
  if (!relativeTo) {
    return time.format("h:mm A");
  }

  const diff = time.diff(relativeTo, "minutes");
  const baseFormat = time.format("h:mm A");

  if (diff < 60) {
    return `${baseFormat} (+${diff} min)`;
  }

  if (diff <= 1440) {
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    if (mins === 0) {
      return `${baseFormat} (+${hours} hr${hours > 1 ? "s" : ""})`;
    }
    return `${baseFormat} (+${hours}h ${mins}m)`;
  }

  const days = Math.round(diff / 1440);
  return `${baseFormat} (+${days} day${days > 1 ? "s" : ""})`;
}
